import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setAdminData,
  setLoading,
  setError,
  clearAdminData,
} from '../redux/slices/adminDataSlice';
import { API_BASE_URL } from '../constants';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import {
  UserGroupIcon,
  UserIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  BookOpenIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const AnimatedCounter = ({ value, duration = 2.5 }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <span ref={ref}>
      {inView ? <CountUp end={value || 0} duration={duration} separator="," /> : 0}
    </span>
  );
};

const StatCard = ({ title, value, icon: Icon, color, gradient }) => (
  <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
    <div className={`absolute inset-0 opacity-10 ${gradient}`}></div>
    <div className="relative p-8 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {title}
        </p>
        <p className="mt-3 text-5xl font-extrabold text-gray-900 dark:text-white">
          <AnimatedCounter value={value} />
        </p>
      </div>
      <div className={`p-5 rounded-2xl ${color.replace('text-', 'bg-') + '/10'}`}>
        <Icon className={`w-14 h-14 ${color}`} />
      </div>
    </div>
    <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 opacity-10 blur-3xl animate-pulse"></div>
  </div>
);

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const {
    totalStudents,
    maleStudentCount,
    femaleStudentCount,
    totalTeachers,
    maleTeacherCount,
    femaleTeacherCount,
    totalClasses,
    juniorClassCount,
    seniorClassCount,
    loading,
    error,
  } = useSelector((state) => state.adminData);

  useEffect(() => {
    const fetchAdminData = async () => {
      dispatch(setLoading(true));
      try {
        const response = await fetch(`${API_BASE_URL}/api/web/admin/dashboard-data-counts`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          dispatch(setAdminData(data));
        } else {
          dispatch(setError(data.message || 'Failed to fetch data'));
        }
      } catch (err) {
        dispatch(setError(err.message));
      }
    };

    fetchAdminData();
    return () => dispatch(clearAdminData());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-24 w-24 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="text-red-600 text-2xl font-bold">{error}</div>
      </div>
    );
  }

  // Data for Charts
  const studentGenderData = [
    { name: 'Male', value: maleStudentCount, color: '#3B82F6' },
    { name: 'Female', value: femaleStudentCount, color: '#EC4899' },
  ];

  const teacherGenderData = [
    { name: 'Male', value: maleTeacherCount, color: '#10B981' },
    { name: 'Female', value: femaleTeacherCount, color: '#F59E0B' },
  ];

  const classDistribution = [
    { name: 'Junior', count: juniorClassCount, fill: '#8B5CF6' },
    { name: 'Senior', count: seniorClassCount, fill: '#EC4899' },
  ];

  const stats = [
    { title: 'Total Students', value: totalStudents, icon: UserGroupIcon, color: 'text-indigo-600' },
    { title: 'Male Students', value: maleStudentCount, icon: UserIcon, color: 'text-blue-600' },
    { title: 'Female Students', value: femaleStudentCount, icon: UserIcon, color: 'text-pink-600' },
    { title: 'Total Teachers', value: totalTeachers, icon: AcademicCapIcon, color: 'text-emerald-600' },
    { title: 'Male Teachers', value: maleTeacherCount, icon: UserIcon, color: 'text-teal-600' },
    { title: 'Female Teachers', value: femaleTeacherCount, icon: UserIcon, color: 'text-amber-600' },
    { title: 'Total Classes', value: totalClasses, icon: BuildingOfficeIcon, color: 'text-purple-600' },
    { title: 'Junior Classes', value: juniorClassCount, icon: BookOpenIcon, color: 'text-violet-600' },
    { title: 'Senior Classes', value: seniorClassCount, icon: TrophyIcon, color: 'text-rose-600' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:to-indigo-950 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">
            Admin Dashboard
          </h1>
          <p className="mt-4 text-xl text-gray-600 dark:text-gray-300">
            Real-time insights • Updated {new Date().toLocaleString()}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {stats.map((stat, i) => (
            <StatCard key={i} {...stat} />
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Student Gender */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Student Gender Ratio</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={studentGenderData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {studentGenderData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v} students`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Teacher Gender */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Teacher Gender Ratio</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={teacherGenderData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {teacherGenderData.map((entry, i) => (
                    <Cell key={`cell-${i}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v} teachers`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Class Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Class Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={classDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" radius={[10, 10, 0, 0]}>
                  {classDistribution.map((entry, i) => (
                    <Cell key={`bar-${i}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-16 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Your School • All data is live and secure
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;